import {
  createContext,
  Dispatch,
  memo,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FilterInput } from '@kinetic-ui/data-layer';
import { FormValues } from '@qfa/forms';
import { UpdateConfig } from 'components/Table/contexts/ColumnsContext/types';
import { PREFERENCE_KEYS, SetState } from 'types';
import { FilterConfig } from '../filterConfigs';
import { ControlsDrawerState, useControlsDrawerState } from '../hooks/useControlsDrawerState';
import { FiltersContextProvider } from './filtersContext';
import { SavedFilterProvider } from './savedFiltersContext';

export type CombinedTableControlsContextValue = {
  filters?: boolean;
  columns?: boolean;
  search?: boolean;

  columnsPreferenceKey: PREFERENCE_KEYS;
  columnsRef: MutableRefObject<UpdateConfig[]>;

  activeContent: 'filters' | 'columns';
  setActiveContent: SetState<'filters' | 'columns'>;

  onSearchChange?: Dispatch<string>;
};

type SearchContextValue = {
  searchText: string;
};

const CombinedTableControlsContext = createContext<CombinedTableControlsContextValue | null>(null);
const SearchContext = createContext<SearchContextValue | null>(null);

export const useCombinedTableControlsContext = () => {
  const context = useContext(CombinedTableControlsContext);
  if (!context) {
    throw new Error(
      'useCombinedTableControlsContext must be used within a CombinedTableControlsProvider'
    );
  }
  return context;
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a CombinedTableControlsProvider');
  }
  return context;
};

const ControlsDrawerContext = createContext<ControlsDrawerState | null>(null);
export const useControlsDrawerContext = () => {
  const context = useContext(ControlsDrawerContext);
  if (!context) {
    throw new Error('useControlDrawerContext must be used within a ControlsDrawerContext.Provider');
  }
  return context;
};

export type CombinedTableControlsProviderProps<TForm extends FormValues> = {
  /** the preference key for the column state */
  columnsPreferenceKey: PREFERENCE_KEYS;
  /** Mutable Ref containing the column configs for the table.
   *
   * used for exports where you need the columns */
  columnsRef: MutableRefObject<UpdateConfig[]>;
  /** the preference key for the filters/savedFilters state */
  filtersPreferenceKey?: PREFERENCE_KEYS;
  /** configurations used to power filters form and transformation to FilterInputs
   *
   * SEE: packages\shared\src\components\CombinedTableControls\filterConfigs.ts
   */
  filterConfigs?: FilterConfig<TForm>[];
  /** handler used to receive FilterInputs */
  onFilterInputsChange?: SetState<FilterInput[]>;
  /** handler used to receive searchText */
  onSearchChange?: Dispatch<string>;
};

const EMPTY: FilterConfig[] = [];
const NOOP = () => {};
export const CombinedTableControlsProvider = memo(
  ({
    children,
    columnsPreferenceKey,
    columnsRef,
    filtersPreferenceKey,
    filterConfigs,
    onFilterInputsChange,
    onSearchChange: _onSearchChange,
  }: PropsWithChildren<CombinedTableControlsProviderProps<any>>) => {
    const enableFilters =
      !!filtersPreferenceKey && !!filterConfigs?.length && !!onFilterInputsChange;
    const enableColumns = !!columnsPreferenceKey && !!columnsRef;
    const enableSearch = !!_onSearchChange;

    const [searchText, setSearchText] = useState('');
    const onSearchChange = useCallback(
      (newSearchText: string) => {
        setSearchText(newSearchText);
        _onSearchChange?.(newSearchText);
      },
      [_onSearchChange]
    );

    const controlsDrawer = useControlsDrawerState();

    const [activeContent, setActiveContent] = useState<
      CombinedTableControlsContextValue['activeContent']
    >(() => {
      if (enableFilters) {
        return 'filters';
      }

      return 'columns';
    });

    const stableContextValue = useMemo(
      () => ({
        activeContent,
        setActiveContent,
        columns: enableColumns,
        filters: enableFilters,
        search: enableSearch,
        columnsPreferenceKey,
        columnsRef,
        onSearchChange,
      }),
      [
        activeContent,
        enableColumns,
        enableFilters,
        enableSearch,
        columnsPreferenceKey,
        columnsRef,
        onSearchChange,
      ]
    );

    const searchContextValue = useMemo(() => ({ searchText }), [searchText]);

    return (
      <CombinedTableControlsContext.Provider value={stableContextValue}>
        <FiltersContextProvider
          key={filtersPreferenceKey}
          filterConfigs={filterConfigs ?? EMPTY}
          filtersPreferenceKey={filtersPreferenceKey}
          onFilterInputsChange={onFilterInputsChange ?? NOOP}
        >
          <SearchContext.Provider value={searchContextValue}>
            <SavedFilterProvider
              key={`${filtersPreferenceKey}-savedFilters`}
              preferenceKey={filtersPreferenceKey}
            >
              <ControlsDrawerContext.Provider value={controlsDrawer}>
                {children}
              </ControlsDrawerContext.Provider>
            </SavedFilterProvider>
          </SearchContext.Provider>
        </FiltersContextProvider>
      </CombinedTableControlsContext.Provider>
    );
  }
);
